/**
 * CustomerCard - T069, T152
 *
 * Card component displaying customer summary
 * Mobile-optimized with touch-friendly interactions
 */
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { GlassPanel } from '../ui';
import type { Customer } from '../../hooks/useCustomers';

interface CustomerCardProps {
  customer: Customer;
  /** Compact mode for list views */
  compact?: boolean;
}

export function CustomerCard({ customer, compact = false }: CustomerCardProps) {
  const contactName = [customer.contactFirstName, customer.contactLastName]
    .filter(Boolean)
    .join(' ');

  if (compact) {
    // Compact mobile-optimized card
    return (
      <Link to="/customers/$id" params={{ id: customer.id }}>
        <motion.div
          whileTap={{ scale: 0.98 }}
          className="block"
        >
          <GlassPanel
            hover
            className="transition-all duration-200 active:bg-white/5"
          >
            <div className="flex items-center justify-between gap-3">
              {/* Left: Customer info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-white truncate">{customer.companyName}</span>
                  {customer.isArchived && (
                    <span className="px-1.5 py-0.5 text-xs bg-white/10 text-white/50 rounded shrink-0">
                      Archiv
                    </span>
                  )}
                </div>
                <p className="text-sm text-white/40 truncate mt-0.5">
                  {customer.customerNumber}
                  {contactName && ` · ${contactName}`}
                </p>
              </div>

              {/* Chevron indicator */}
              <svg className="w-4 h-4 text-white/30 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </GlassPanel>
        </motion.div>
      </Link>
    );
  }

  // Full card
  return (
    <Link to="/customers/$id" params={{ id: customer.id }}>
      <motion.div
        whileTap={{ scale: 0.98 }}
        className="block h-full"
      >
        <GlassPanel
          hover
          className="cursor-pointer transition-all hover:scale-[1.01] active:scale-[0.99] h-full"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-medium text-white truncate">
                  {customer.companyName}
                </h3>
                {customer.isArchived && (
                  <span className="px-2 py-0.5 text-xs bg-white/10 text-white/50 rounded">
                    Archiviert
                  </span>
                )}
              </div>
              <p className="text-sm text-white/40 mt-0.5">
                {customer.customerNumber}
              </p>
            </div>
          </div>

          {(contactName || customer.email || customer.phone) && (
            <div className="mt-4 pt-4 border-t border-white/[0.06] space-y-2">
              {contactName && (
                <p className="text-sm text-white/60">{contactName}</p>
              )}
              {customer.email && (
                <p className="text-sm text-white/40 truncate">{customer.email}</p>
              )}
              {customer.phone && (
                <a
                  href={`tel:${customer.phone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-sm text-white/40 hover:text-white block"
                >
                  {customer.phone}
                </a>
              )}
            </div>
          )}
        </GlassPanel>
      </motion.div>
    </Link>
  );
}

export default CustomerCard;
